import * as Icons from "lucide-react";
import { Button } from "@/components/ui/button";
import { FieldEditor } from "./FieldEditor";

// A key like "item_fields" is a GUIDE written by the website: it describes the fields each entry
// of the sibling list ("items") needs, e.g. { image: "required: …", headline: "optional: …" }.
// It is not content — the website ignores it. So it is shown as help, never as an editable form,
// and "Add item" on the list uses it as the shape of a new entry.
const GUIDE = /^(.+)_fields$/;
const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);
const isHint = (v) => typeof v === "string" && /^(required|optional)\b/i.test(v.trim());
const guidesOf = (obj) => Object.keys(obj)
  .map((k) => { const m = k.match(GUIDE); return m && isObj(obj[k]) ? { key: k, list: `${m[1]}s`, fields: obj[k] } : null; })
  .filter(Boolean);
// New entry built from a guide: booleans for "true = …" fields, text otherwise.
export const templateFromGuide = (fields) => Object.fromEntries(Object.entries(fields).map(([k, h]) => [k, /\btrue\s*=/.test(String(h)) ? false : ""]));
const restoreHint = (k, v) => (isHint(v) ? v : k === "image" ? "required: full https:// image URL or a site path like /home/x.jpg" : "optional");

export function ObjectFields({ obj, onChange, path, skip = [], hints }) {
  const guides = guidesOf(obj);
  const guideKeys = guides.map((g) => g.key);
  const guideFor = (k) => guides.find((g) => g.list === k);
  // Lists that only exist in the guide (no items yet) still get an editor.
  const keys = Array.from(new Set([...Object.keys(obj), ...guides.map((g) => g.list)]))
    .filter((k) => !skip.includes(k) && !guideKeys.includes(k)).sort();

  return (
    <div className="space-y-3">
      {guides.map((g) => {
        // Values typed into the guide by mistake (e.g. an uploaded image) — offer to move them.
        const stray = Object.entries(g.fields).filter(([, v]) => v !== "" && v !== false && !isHint(v));
        if (!stray.length) return null;
        const move = () => {
          const item = { ...templateFromGuide(g.fields), ...Object.fromEntries(stray) };
          const fixed = Object.fromEntries(Object.entries(g.fields).map(([k, v]) => [k, restoreHint(k, v)]));
          onChange({ ...obj, [g.key]: fixed, [g.list]: [...(Array.isArray(obj[g.list]) ? obj[g.list] : []), item] });
        };
        return (
          <div key={`stray-${g.key}`} className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 flex flex-wrap items-center gap-3" data-testid={`cms-stray-${g.key}`}>
            <Icons.TriangleAlert className="h-4 w-4 shrink-0" />
            <span className="flex-1 min-w-[220px]">{stray.map(([k]) => k).join(", ")} {stray.length > 1 ? "were" : "was"} entered in the field guide, so the website can't show {stray.length > 1 ? "them" : "it"}. Move {stray.length > 1 ? "them" : "it"} into a new entry in <b>{g.list}</b>.</span>
            <Button size="sm" className="h-7" onClick={move} data-testid={`cms-move-${g.key}`}>Move into a new entry</Button>
          </div>
        );
      })}
      {keys.map((k) => {
        const g = guideFor(k);
        const value = obj[k] === undefined && g ? [] : obj[k];
        return (
          <div key={k} className="grid grid-cols-1 md:grid-cols-[150px_1fr] gap-1 md:gap-3 items-start">
            <div className="pt-2">
              <p className="text-[11px] font-mono text-muted-foreground break-all">{k}</p>
              {hints?.[k] && <p className="text-[10px] text-muted-foreground/80 leading-snug mt-0.5" data-testid={`cms-hint-${k}`}>{String(hints[k]).replace(/^(required|optional):?\s*/i, (m) => (/required/i.test(m) ? "Required · " : "Optional · "))}</p>}
            </div>
            <div className="min-w-0 space-y-2">
              {g && (
                <p className="text-[11px] text-muted-foreground rounded-md bg-secondary/60 border border-border px-2.5 py-1.5" data-testid={`cms-guide-${g.key}`}>
                  <Icons.Info className="inline h-3 w-3 mr-1 -mt-0.5" />Each entry has: {Object.entries(g.fields).map(([f, h]) => `${f}${isHint(h) && /^required/i.test(h) ? " (required)" : ""}`).join(", ")}. Click <b>Add item</b> to add one — the website shows only entries in this list.
                </p>
              )}
              <FieldEditor name={k} value={value} path={`${path}.${k}`} onChange={(v) => onChange({ ...obj, [k]: v })}
                template={g ? templateFromGuide(g.fields) : undefined} hints={g ? g.fields : undefined} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
